/**
 * Selection Box Overlay
 * 
 * Renders the selection rectangle during window/crossing selection.
 * Blue = Window (left-to-right), Green = Crossing (right-to-left)
 */

import React from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import '../../styles/global.css';
import { useSelectionStore } from './SelectionManager';

export const SelectionBoxOverlay: React.FC = () => {
    const { t } = useTranslation();
    // Narrowed selector (was a whole-store destructure): the overlay must not
    // re-render on unrelated selection-store writes (filters, mode toggles).
    const { selectionBox, isSelecting, mode, lassoPoints } = useSelectionStore(
        useShallow((s) => ({
            selectionBox: s.selectionBox,
            isSelecting: s.isSelecting,
            mode: s.mode,
            lassoPoints: s.lassoPoints,
        }))
    );

    // Lasso mode: freehand polygon rendered as an SVG path.
    if (isSelecting && mode === 'lasso') {
        if (lassoPoints.length < 3) return null;
        const path = lassoPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + ' Z';
        return (
            <svg
                className="selection-lasso-overlay"
            >
                <path d={path} fill="rgba(0,170,255,0.08)" stroke="#00aaff" strokeWidth={1.5} strokeDasharray="6 3" />
            </svg>
        );
    }

    if (!isSelecting || !selectionBox) return null;

    const { start, end, mode: boxMode } = selectionBox;

    // Calculate box dimensions
    const left = Math.min(start.x, end.x);
    const top = Math.min(start.y, end.y);
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);

    const isWindow = boxMode === "window";
    const boxClass = `selection-box ${isWindow ? 'selection-box--window' : 'selection-box--crossing'}`;
    const labelClass = `selection-box__label ${isWindow ? 'selection-box__label--window' : 'selection-box__label--crossing'}`;

    return (
        <div
            className={boxClass}
            style={{
                left: `${left}px`,
                top: `${top}px`,
                width: `${width}px`,
                height: `${height}px`,
            }}
        >
            <div className={labelClass}>
                {/* Marquee labels ride i18n (Faz 11.5 — the D2 rule). */}
                {isWindow ? t('selection.window') : t('selection.crossing')}
            </div>
        </div>
    );
};

export default SelectionBoxOverlay;

